import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Polyline, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { apiError } from "@/lib/api";
import { reverseGeocode } from "@/lib/geo";
import { TrackingLogs } from "@/components/TrackingLogs";
import { useList, useInvalidate } from "@/lib/hooks";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPinLine, Copy, PaperPlaneTilt, StopCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = { sent: "bg-amber-100 text-amber-700", active: "bg-emerald-100 text-emerald-700", expired: "bg-slate-100 text-slate-500", stopped: "bg-slate-100 text-slate-500" };
const LABEL = { sent: "Link sent", active: "Live", expired: "Expired", stopped: "Stopped" };
const ago = (iso) => { if (!iso) return "—"; const s = Math.round((Date.now() - new Date(iso)) / 1000); return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : new Date(iso).toLocaleString(); };
const copy = (url, quiet) => { navigator.clipboard.writeText(url); if (!quiet) toast.success("Tracking link copied"); };

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 14), { animate: true }); }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function NewTracking({ onCreated }) {
  const [f, setF] = useState({ name: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/tracking", f);
      if (data.sms_sent) toast.success(`Tracking link texted to ${data.phone}`);
      else { copy(data.url, true); toast.message("Link copied. Text messages aren't connected yet, so paste it to the driver."); }
      setF({ name: "", phone: "", note: "" }); onCreated(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-3" data-testid="tracking-new-form">
      <Input required placeholder="Driver / carrier name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white border-slate-300" data-testid="tracking-name" />
      <Input required placeholder="US / Canada phone, e.g. +1 (919) 555-0100" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="tracking-phone" />
      <Input placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="bg-white border-slate-300" data-testid="tracking-note" />
      <Button type="submit" disabled={busy} className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="tracking-send-btn"><PaperPlaneTilt size={16} className="mr-1.5" />{busy ? "Sending…" : "Send Tracking Link (72 h)"}</Button>
    </form>
  );
}

function LiveMap({ id, onDeleted }) {
  const { data: t } = useList(["tracking", id], `/tracking/${id}`, { refetchInterval: 10000 });
  const invalidate = useInvalidate();
  const [fallback, setFallback] = useState("");
  const la = t?.last?.lat, ln = t?.last?.lng, hasAddr = !!t?.last?.address;
  useEffect(() => { if (la != null && !hasAddr) reverseGeocode(la, ln).then(setFallback); }, [la, ln, hasAddr]);
  if (!t) return <div className="h-full grid place-items-center text-slate-400">Loading…</div>;
  const last = t.last && { ...t.last, address: t.last.address || fallback };
  const pos = last ? [last.lat, last.lng] : null;
  const stop = async () => {
    if (!window.confirm(`Stop tracking ${t.name}? The link will stop working.`)) return;
    try { await api.post(`/tracking/${id}/stop`); invalidate(["tracking"]); invalidate(["trackings"]); toast.success("Tracking stopped"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the tracking history for ${t.name}?`)) return;
    try { await api.delete(`/tracking/${id}`); invalidate(["trackings"]); onDeleted(); toast.success("Tracking deleted"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-col h-full" data-testid="tracking-live">
      <div className="flex flex-wrap items-start justify-between gap-3 p-5 border-b border-slate-200">
        <div className="min-w-0">
          <p className="font-heading font-bold text-lg text-slate-900">{t.name} <span className={`ml-2 align-middle px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE[t.status]}`} data-testid="tracking-live-status">{LABEL[t.status]}{t.status === "active" && t.paused_at ? " · paused" : ""}</span></p>
          <p className="text-sm text-slate-700 mt-1" data-testid="tracking-address"><MapPinLine size={15} className="inline -mt-0.5 mr-1 text-blue-600" />{last?.address || (pos ? `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}` : "Waiting for the driver to accept the link")}</p>
          {last && <p className="text-xs text-slate-500 mt-0.5" data-testid="tracking-updated">Updated {ago(last.at)} · accuracy ±{Math.round(last.accuracy)} m{last.speed != null ? ` · ${Math.round(last.speed * 2.237)} mph` : ""} · {pos[0].toFixed(5)}, {pos[1].toFixed(5)}</p>}
          {t.note && <p className="text-xs text-slate-600 mt-0.5" data-testid="tracking-note-view">Note: {t.note}</p>}
          <p className="text-xs text-slate-400 mt-0.5">{t.phone} · expires {new Date(t.expires_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => copy(t.url)} className="border-slate-300 bg-white" data-testid="tracking-copy-link"><Copy size={15} className="mr-1" />Copy Link</Button>
          {!["sent", "active"].includes(t.status) && <Button size="sm" variant="outline" onClick={remove} className="border-slate-300 bg-white text-slate-600" data-testid="tracking-delete"><Trash size={15} className="mr-1" />Delete</Button>}
          {["sent", "active"].includes(t.status) && <Button size="sm" variant="outline" onClick={stop} className="border-red-200 bg-white text-red-600" data-testid="tracking-stop"><StopCircle size={15} className="mr-1" />Stop</Button>}
        </div>
      </div>
      <div className="flex-1 min-h-[420px]">
        <MapContainer center={pos || [39.5, -98.35]} zoom={pos ? 14 : 4} className="h-full w-full" scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {t.points?.length > 1 && <Polyline positions={t.points.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.7 }} />}
          {pos && <Circle center={pos} radius={Math.max(last.accuracy, 5)} pathOptions={{ color: "#10b981", fillOpacity: 0.12, weight: 1 }} />}
          {pos && <CircleMarker center={pos} radius={9} pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#10b981", fillOpacity: 1 }}><Popup>{t.name}<br />{last.address}</Popup></CircleMarker>}
          <Recenter pos={pos} />
        </MapContainer>
      </div>
      <TrackingLogs id={id} name={t.name} />
    </div>
  );
}

export default function CarrierTracking() {
  const { data: rows = [] } = useList("trackings", "/tracking", { refetchInterval: 15000 });
  const invalidate = useInvalidate();
  const [sel, setSel] = useState(null);
  const current = sel || rows[0]?.id;
  return (
    <div className="space-y-6" data-testid="carrier-tracking-page">
      <PageHeader title="Carrier Tracking" subtitle="Text a driver a 72-hour link and watch their truck live on the map." />
      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div className="space-y-6">
          <Panel className="p-5"><h3 className="font-heading font-bold text-slate-900 mb-3">New Tracking Link</h3><NewTracking onCreated={(d) => { invalidate(["trackings"]); setSel(d.id); }} /></Panel>
          <Panel className="p-2 max-h-[520px] overflow-y-auto" data-testid="tracking-list">
            {rows.length === 0 ? <div className="p-4"><EmptyState title="No tracking links yet" hint="Send one to start tracking a truck." icon={MapPinLine} /></div> : rows.map((t) => (
              <button key={t.id} onClick={() => setSel(t.id)} className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${current === t.id ? "bg-blue-50" : "hover:bg-slate-50"}`} data-testid={`tracking-row-${t.id}`}>
                <div className="flex items-center justify-between gap-2"><span className="font-semibold text-slate-900 truncate">{t.name}</span><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TONE[t.status]}`}>{LABEL[t.status]}</span></div>
                <p className="text-xs text-slate-500 truncate">{t.last?.address || t.phone}</p>
                <p className="text-[11px] text-slate-400">{t.last ? `Updated ${ago(t.last.at)}` : `Sent ${ago(t.created_at)}`} · by {t.created_by_name || "—"}</p>
              </button>
            ))}
          </Panel>
        </div>
        <Panel className="overflow-hidden min-h-[560px]">{current ? <LiveMap id={current} key={current} onDeleted={() => setSel(null)} /> : <div className="h-full grid place-items-center text-slate-400 p-10">Send a tracking link to see a truck here.</div>}</Panel>
      </div>
    </div>
  );
}
